import { Link } from "react-router";
import type { StepStatus } from "../../api/pathway";
import { Banner } from "../../components/Banner";
import { Guide } from "../../components/Guide";
import { InstallCard } from "../../components/InstallCard";

interface HubScreenProps {
  trackLabel: string;
  firstName: string;
  gig: StepStatus[];
  finance: StepStatus[];
  doneCount: number;
  totalCount: number;
  percent: number;
  workEnabled: boolean;
  pathwayComplete: boolean;
}

function StepCard({
  prefix,
  step,
  to,
  note,
}: {
  prefix: string;
  step: StepStatus;
  to: string;
  note?: string;
}) {
  const open = step.state !== "locked";
  const status = step.state === "done" ? "Done" : step.state === "active" ? "Do this" : "Locked";
  const badge =
    step.state === "done"
      ? "bg-positive text-white"
      : step.state === "active"
        ? "bg-accent text-on-accent"
        : "bg-surface-high text-text-muted";
  const body = (
    <>
      <span
        className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full font-bold ${badge}`}
      >
        {step.state === "done" ? "✓" : step.step}
      </span>
      <span className="flex-1">
        <span className="block text-[11px] font-bold uppercase tracking-[0.08em] text-text-muted">
          {prefix} · Step {step.step}
          {prefix === "GIG" && step.step >= 5 ? " · while earning" : ""}
        </span>
        <span className="block text-[15px] font-bold text-text">{step.name}</span>
        <span className="block text-[13px] text-text-muted">
          {step.desc}
          {note ? ` ${note}` : ""}
        </span>
      </span>
      <span className="text-[12px] font-bold text-text-muted">{status}</span>
    </>
  );
  const className = `flex items-center gap-3.5 rounded-[var(--radius-card)] border p-4 ${
    step.state === "active" ? "border-accent bg-surface" : "border-border bg-surface"
  } ${open ? "hover:border-accent" : "opacity-60"}`;

  return open ? (
    <Link
      to={to}
      className={className}
      aria-label={`${prefix} step ${step.step}: ${step.name}, ${status}`}
    >
      {body}
    </Link>
  ) : (
    <div className={className} aria-label={`${prefix} step ${step.step}: ${step.name}, locked`}>
      {body}
    </div>
  );
}

/** The combined hub: gig and finance steps interleaved, with progress and the Work-Enabled banner. */
export function HubScreen(props: HubScreenProps) {
  const { gig, finance } = props;
  return (
    <div>
      <div className="mb-4">
        <div className="text-[12px] font-bold uppercase tracking-[0.08em] text-kicker">
          {props.trackLabel}
          {props.firstName ? ` · ${props.firstName}` : ""}
        </div>
        <h2 className="mt-1 text-[22px] text-accent">Your road to Work-Enabled and financed</h2>
        <div
          className="mt-3 h-2.5 overflow-hidden rounded-full bg-surface-high"
          role="progressbar"
          aria-valuenow={props.percent}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <i className="block h-full bg-accent" style={{ width: `${props.percent}%` }} />
        </div>
        <div className="mt-1.5 text-[13px] text-text-muted">
          {props.doneCount} of {props.totalCount} steps complete
          {props.pathwayComplete ? " · Pathway complete" : ""}
        </div>
      </div>
      <Guide guideKey="hub" done={gig[0]?.state === "done"} />
      <InstallCard />
      {props.workEnabled ? (
        <Banner tone="good">
          ✓ You're Work-Enabled.{" "}
          <Link to="/launch-plan" className="underline">
            Open your Launch Plan
          </Link>
        </Banner>
      ) : null}
      {props.pathwayComplete ? (
        <Banner tone="note">
          The optional{" "}
          <Link to="/grow-library" className="font-bold text-accent underline">
            Grow Library
          </Link>{" "}
          is now open, for growing your freelance business further.
        </Banner>
      ) : null}
      <div className="flex flex-col gap-2.5">
        {gig.map((step, index) => {
          const fin = finance[index];
          const gigDone = (n: number) => gig[n - 1]?.state === "done";
          return (
            <div key={step.step} className="contents">
              <StepCard prefix="GIG" step={step} to={`/gig/${step.step}`} />
              {fin ? (
                <StepCard
                  prefix="FINANCE"
                  step={fin}
                  to={`/fin/${fin.step}`}
                  {...(fin.step >= 5 && fin.state !== "done" && !gigDone(fin.step)
                    ? { note: `(completes after gig Step ${fin.step})` }
                    : {})}
                />
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}
